import React from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/app/paths';
import { AdminDataGrid } from '../../shared/AdminDataGrid';
import { USER_IMPORT_STATUSES } from '../../users/api/userImportsApi';
import { adminImportsApi, useListImportsQuery } from '../api/importsApi';
import { importColumns } from './columns';

// While an import on the page is still going, the page refreshes this often
const ACTIVE_POLL_MS = 5000;

const isImportActive = ({ status }) =>
	status === USER_IMPORT_STATUSES.queued || status === USER_IMPORT_STATUSES.running;

/**
 * The import history grid: one page of imports, newest first, paged, sorted and filtered
 * (by type and status) on the server (listImports, with listParams from the page). While
 * any import on the page is queued or running, it refreshes every few seconds, so its
 * status and progress move on. Clicking a row opens that import's report
 * (/admin/imports/:importId).
 *
 * @param {object} props
 * @param {object} props.listParams from useAdminListParams
 * @param {object} props.gridProps from useAdminListParams
 */
export const ImportsTable = ({ listParams, gridProps }) => {
	const navigate = useNavigate();
	// The current page from the cache (without subscribing), to decide whether to keep polling
	const { data: cachedPage } = adminImportsApi.endpoints.listImports.useQueryState(listParams);
	const { data, isLoading, isError } = useListImportsQuery(listParams, {
		pollingInterval: cachedPage?.rows.some(isImportActive) ? ACTIVE_POLL_MS : 0,
	});

	return (
		<AdminDataGrid
			table='imports'
			columns={importColumns}
			rows={data?.rows ?? []}
			rowCount={data?.total ?? 0}
			paginationMode='server'
			sortingMode='server'
			isLoading={isLoading}
			isError={isError}
			objectType='imports'
			showToolbar={false}
			{...gridProps}
			onRowClick={({ id }) => navigate(`${paths.admin}/imports/${id}`)}
		/>
	);
};
